import { useEffect, useRef, useState } from "react";
import { W2IngestPortal } from "@/components/dashboard/W2IngestPortal";
import { SpecializedVault } from "@/components/dashboard/SpecializedVault";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCountUp } from "@/lib/useCountUp";
import { toast } from "sonner";
import { putShot, deleteShot, listShotsForUser } from "@/lib/paystubBatchStorage";

// Convert a Blob to a base64 string (dataURL prefix stripped).
async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

// Convert a base64 payload back into a Blob for URL.createObjectURL rehydration.
function base64ToBlob(b64, mime = "image/jpeg") {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// ---------- Storage quota probe ----------
// Returns { supported, free, quota, warning } — warning=true when free < 50MB.
async function probeStorageQuota() {
  if (!navigator.storage?.estimate) return { supported: false };
  try {
    const est = await navigator.storage.estimate();
    const quota = est.quota || 0;
    const usage = est.usage || 0;
    const free = Math.max(0, quota - usage);
    const FIFTY_MB = 50 * 1024 * 1024;
    return { supported: true, free, quota, usage, warning: free < FIFTY_MB };
  } catch {
    return { supported: false };
  }
}
import { Upload, Sparkles, AlertTriangle, TrendingUp, ShieldCheck, Camera, X as CloseX, Aperture, RotateCw, Minimize2, Maximize2, HardDrive, Building2, Calendar, RotateCcw } from "lucide-react";
import { ConfirmResetDialog } from "@/components/ConfirmResetDialog";

/**
 * PaycheckOptimizerNode
 * ---------------------
 * Premium-Shield-only widget that visualizes the user's live paystub trajectory:
 *   • Rolling YTD-withheld odometer (useCountUp)
 *   • 12-period gross-vs-withheld SVG area/line trajectory
 *   • Underpayment-drift alert badge (red when > 0, emerald otherwise)
 *   • Upload dropzone that hits POST /api/v1/paycheck-stubs/upload
 *   • Fires a small gold confetti burst on successful ingestion via a
 *     window CustomEvent "paycheck-stub-ingested" — Dashboard listens.
 */
const CURRENCY = (c) => (c === "USD" || !c ? "$" : c === "EUR" ? "€" : c === "GBP" ? "£" : `${c} `);

// Re-encode a File/Blob to standard JPEG via an in-memory canvas so iOS Safari
// HEIC uploads and non-standard camera roll formats always land on the server
// as portable, GPT-5.2-Vision-readable JPEGs.  Returns the original file
// unchanged if it's already a JPEG (fast path, no double-compress artifacts).
async function reencodeToJpegIfNeeded(file) {
  if (!file) return file;
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  const isHeic = type.includes("heic") || type.includes("heif") || name.endsWith(".heic") || name.endsWith(".heif");
  const isPlainJpeg = type === "image/jpeg" || type === "image/jpg";
  // Only skip the canvas hop when we already have a plain JPEG that isn't
  // HEIC-in-disguise.  Everything else (PNG, WEBP, HEIC, unknown) gets
  // funneled through the canvas re-encode.
  if (isPlainJpeg && !isHeic) return file;
  try {
    let bitmap;
    if (typeof createImageBitmap === "function") {
      bitmap = await createImageBitmap(file);
    } else {
      // Safari <15 fallback via HTMLImageElement.
      bitmap = await new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { resolve(img); URL.revokeObjectURL(url); };
        img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
        img.src = url;
      });
    }
    const w = bitmap.width || bitmap.naturalWidth || 1600;
    const h = bitmap.height || bitmap.naturalHeight || 1200;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const jpegBlob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85));
    if (!jpegBlob) return file;
    return new File([jpegBlob], (name.replace(/\.(heic|heif|png|webp)$/i, "") || "paystub") + ".jpg", { type: "image/jpeg" });
  } catch {
    // If decode blows up (rare — e.g. corrupt HEIC), fall back to the raw
    // upload so the server still gets a chance to handle it.
    return file;
  }
}

function TrajectorySVG({ trajectory, currencySymbol }) {
  if (!trajectory || trajectory.length < 2) {
    return (
      <div className="h-40 flex items-center justify-center text-slate-500 text-sm">
        Upload a second paystub to see the trajectory line.
      </div>
    );
  }
  const w = 640, h = 160, padX = 24, padY = 20;
  const grosses = trajectory.map((t) => t.gross);
  const withhelds = trajectory.map((t) => t.withheld);
  const maxY = Math.max(1, ...grosses, ...withhelds);
  const xStep = (w - padX * 2) / (trajectory.length - 1);
  const yFor = (v) => h - padY - (v / maxY) * (h - padY * 2);

  const path = (arr) => arr.map((v, i) => `${i === 0 ? "M" : "L"} ${padX + i * xStep} ${yFor(v)}`).join(" ");
  const grossD = path(grosses);
  const withheldD = path(withhelds);
  const areaD = `${grossD} L ${padX + (trajectory.length - 1) * xStep} ${h - padY} L ${padX} ${h - padY} Z`;

  return (
    <svg
      data-testid="paycheck-trajectory-svg"
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      className="block"
      aria-label="Gross pay and federal withholding trajectory"
    >
      <defs>
        <linearGradient id="stub-gross-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a855f7" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="stub-gross-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#c084fc" />
          <stop offset="100%" stopColor="#06B6D4" />
        </linearGradient>
      </defs>
      <path d={areaD} fill="url(#stub-gross-area)" />
      <path d={grossD} stroke="url(#stub-gross-line)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d={withheldD} stroke="#f4c542" strokeWidth="2" strokeDasharray="4 4" fill="none" strokeLinecap="round" />
      {trajectory.map((t, i) => (
        <g key={i}>
          <circle cx={padX + i * xStep} cy={yFor(t.gross)} r="3" fill="#c084fc" />
          <circle cx={padX + i * xStep} cy={yFor(t.withheld)} r="2.5" fill="#f4c542" />
        </g>
      ))}
      <text x={padX} y={h - 4} fontSize="9" fontFamily="ui-monospace,monospace" fill="#64748b">
        {trajectory[0].period_end || "earliest"}
      </text>
      <text x={w - padX} y={h - 4} fontSize="9" fontFamily="ui-monospace,monospace" fill="#64748b" textAnchor="end">
        {currencySymbol}{Math.round(maxY).toLocaleString()} max
      </text>
    </svg>
  );
}

export function PaycheckOptimizerNode() {
  const { user } = useAuth();
  const userId = user?.user_id;
  const [forecast, setForecast] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [isLandscape, setIsLandscape] = useState(false);
  const [capturedBlobs, setCapturedBlobs] = useState([]);
  const [focusReticle, setFocusReticle] = useState(null); // {x, y, id, ok}
  const [shipping, setShipping] = useState(false);
  const [flashKey, setFlashKey] = useState(0);
  const [shipProgress, setShipProgress] = useState({ current: 0, total: 0 });
  const [minimized, setMinimized] = useState(false);
  const [resumePrompt, setResumePrompt] = useState(null); // {shots: []}
  const [quotaWarning, setQuotaWarning] = useState(null); // {freeBytes, quotaBytes}
  const [recentBatch, setRecentBatch] = useState(null);   // {stubs: [], openedAt}
  const [drawerOpen, setDrawerOpen] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Track device orientation so the composition guide and video frame
  // programmatically widen into a 4:3 landscape crop when the user rotates the
  // phone.  Prefers screen.orientation API, falls back to matchMedia for older
  // Safari + onorientationchange for iOS 15-.
  useEffect(() => {
    const compute = () => {
      let landscape = false;
      // matchMedia is the most reliable primary signal because it correctly
      // reflects the current viewport dimensions in every browser (mobile +
      // desktop).  screen.orientation is a physical-device signal and can lag
      // or misreport in some Chromium contexts; only use it as a fallback.
      if (window.matchMedia) {
        landscape = window.matchMedia("(orientation: landscape)").matches;
      } else if (window.screen?.orientation?.type) {
        landscape = window.screen.orientation.type.startsWith("landscape");
      } else if (typeof window.orientation === "number") {
        landscape = Math.abs(window.orientation) === 90;
      }
      setIsLandscape(landscape);
    };
    compute();
    const mm = window.matchMedia?.("(orientation: landscape)");
    const onChange = () => compute();
    mm?.addEventListener?.("change", onChange);
    window.screen?.orientation?.addEventListener?.("change", onChange);
    window.addEventListener("orientationchange", onChange);
    window.addEventListener("resize", onChange);
    return () => {
      mm?.removeEventListener?.("change", onChange);
      window.screen?.orientation?.removeEventListener?.("change", onChange);
      window.removeEventListener("orientationchange", onChange);
      window.removeEventListener("resize", onChange);
    };
  }, []);

  const loadForecast = async () => {
    try {
      const { data } = await api.get("/v1/paycheck-stubs/forecast");
      setForecast(data);
    } catch (e) {
      // 403 = tier gate; upstream <PremiumShieldGate> already handles that path.
    }
  };

  useEffect(() => {
    loadForecast();
    const onRefresh = () => loadForecast();
    window.addEventListener("paycheck-stub-ingested", onRefresh);
    return () => window.removeEventListener("paycheck-stub-ingested", onRefresh);
  }, []);

  // Auto-resume: scan BOTH IndexedDB (device-local) AND Mongo (cross-device
  // sync) on mount for un-shipped batch shots from a prior session.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const [localRows, remoteRes] = await Promise.all([
        listShotsForUser(userId).catch(() => []),
        api.get("/v1/paycheck-stubs/pending-batch").then((r) => r.data?.shots || []).catch(() => []),
      ]);
      if (cancelled) return;
      // Merge — remote takes precedence for shot_ids that exist in both, since
      // remote is the cross-device source of truth.  Rebuild remote shots as
      // Blobs so they slot into the same rehydration path as local shots.
      const map = new Map();
      for (const l of localRows) {
        map.set(l.shotId, { source: "local", shotId: l.shotId, blob: l.blob, capturedAt: l.capturedAt });
      }
      for (const r of remoteRes) {
        try {
          const blob = base64ToBlob(r.blob_b64, r.mime || "image/jpeg");
          map.set(r.shot_id, { source: "remote", shotId: r.shot_id, blob, capturedAt: r.captured_at });
        } catch {
          // skip un-decodable rows silently
        }
      }
      const merged = Array.from(map.values());
      if (merged.length) setResumePrompt({ shots: merged });
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Probe browser storage quota on mount and whenever camera opens.
  useEffect(() => {
    if (!cameraOpen) return;
    let cancelled = false;
    (async () => {
      const q = await probeStorageQuota();
      if (cancelled) return;
      setQuotaWarning(q.warning ? { free: q.free, quota: q.quota } : null);
    })();
    return () => { cancelled = true; };
  }, [cameraOpen]);

  const acceptResume = () => {
    if (!resumePrompt?.shots?.length) return;
    const rehydrated = resumePrompt.shots.map((row) => ({
      id: row.shotId,
      blob: row.blob,
      thumbUrl: URL.createObjectURL(row.blob),
      status: "queued",
      errorMessage: null,
    }));
    setCapturedBlobs(rehydrated);
    setCameraOpen(true);
    setMinimized(false);
    setResumePrompt(null);
    toast.info(`Resumed ${rehydrated.length} paystub${rehydrated.length === 1 ? "" : "s"} from your last session`);
  };

  const dismissResume = async () => {
    if (!resumePrompt?.shots?.length) { setResumePrompt(null); return; }
    // Wipe both local and remote — the user has explicitly discarded.
    await Promise.all([
      ...resumePrompt.shots.map((r) => deleteShot({ userId, shotId: r.shotId })),
      api.delete("/v1/paycheck-stubs/pending-batch").catch(() => null),
    ]);
    setResumePrompt(null);
  };

  // Raw upload helper — always throws on failure so callers can distinguish
  // success from error.  Both handleUpload (single-file file-picker path) and
  // uploadOneShot (per-thumbnail batch/retry path) delegate to this.
  const uploadRaw = async (rawFile) => {
    // iOS Safari can hand us a HEIC blob from <input type=file>.  Force a
    // canvas re-encode to JPEG so the backend GPT-5.2 Vision call always
    // receives a portable image, regardless of camera source encoding.
    const file = await reencodeToJpegIfNeeded(rawFile);
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/v1/paycheck-stubs/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    // Refresh forecast + dispatch confetti event on every successful ingest
    await loadForecast();
    window.dispatchEvent(new CustomEvent("paycheck-stub-ingested", { detail: data.stub }));
    return data;
  };

  const handleUpload = async (rawFile) => {
    if (!rawFile) return;
    setUploading(true);
    const toastId = toast.loading("Ingesting paystub · GPT-5.2 Vision extracting…");
    try {
      const data = await uploadRaw(rawFile);
      toast.success(`Paystub ingested · gross ${CURRENCY(data.stub?.currency)}${(data.stub?.gross_pay || 0).toFixed(2)}`, { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Paystub ingestion failed", { id: toastId });
      throw e; // let batch/retry callers know
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ---------- Native camera capture (mobile-first, back-facing) ----------
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  };

  const openCamera = async () => {
    setCameraError("");
    setCameraReady(false);
    setMinimized(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera not supported on this device");
      return;
    }
    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCameraReady(true);
    } catch (e) {
      const msg =
        e?.name === "NotAllowedError" ? "Camera permission denied — enable it in browser settings"
        : e?.name === "NotFoundError" ? "No camera hardware detected on this device"
        : "Could not start camera stream";
      setCameraError(msg);
      toast.error(msg);
    }
  };

  const closeCamera = () => {
    stopCamera();
    setCameraOpen(false);
    setCameraError("");
    setMinimized(false);
    // Discard any un-shipped in-memory blobs so a re-open starts clean.
    setCapturedBlobs([]);
    setFocusReticle(null);
  };

  // ---------- Multi-shot: snap-to-batch instead of immediate upload ----------
  const snapToBatch = async () => {
    const video = videoRef.current;
    if (!video || !streamRef.current || shipping) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      toast.error("Canvas unavailable in this browser");
      return;
    }
    ctx.drawImage(video, 0, 0, w, h);
    const blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85));
    if (!blob) {
      toast.error("Snapshot capture failed");
      return;
    }
    const shotId = `shot-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setCapturedBlobs((prev) => [...prev, {
      id: shotId,
      blob,
      thumbUrl: URL.createObjectURL(blob),
      status: "queued",       // queued | uploading | ok | error
      errorMessage: null,
    }]);
    // Persist to IndexedDB so a tab crash mid-batch doesn't lose work.
    putShot({ userId, shotId, blob, capturedAt: Date.now() });
    // Cross-device sync — also stream to Mongo pending_paystub_batches so a
    // batch started on phone can be finished on desktop.  Best-effort; skip
    // the sync if the blob is oversized (server-side guard rejects >6MB).
    if (blob.size <= 6 * 1024 * 1024) {
      blobToBase64(blob).then((b64) =>
        api.post("/v1/paycheck-stubs/pending-batch", {
          shot_id: shotId,
          blob_b64: b64,
          mime: "image/jpeg",
          captured_at: Date.now(),
          size_bytes: blob.size,
        }).catch(() => {})
      );
    }
    setFlashKey((k) => k + 1); // brief white flash overlay
  };

  const discardShot = (id) => {
    setCapturedBlobs((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target?.thumbUrl) URL.revokeObjectURL(target.thumbUrl);
      return prev.filter((s) => s.id !== id);
    });
    deleteShot({ userId, shotId: id });
    api.delete(`/v1/paycheck-stubs/pending-batch/${id}`).catch(() => {});
  };

  // Inline upload helper — used by both the initial batch ship and the
  // per-thumbnail retry gate.  Updates the shot's status by ID.  Calls
  // uploadRaw directly so we don't spam per-shot loading toasts during a
  // batch.
  const uploadOneShot = async (shot, indexLabel = "solo") => {
    setCapturedBlobs((prev) => prev.map((s) => (s.id === shot.id ? { ...s, status: "uploading", errorMessage: null } : s)));
    const file = new File([shot.blob], `paystub-batch-${indexLabel}-${Date.now()}.jpg`, { type: "image/jpeg" });
    try {
      const data = await uploadRaw(file);
      setCapturedBlobs((prev) => prev.map((s) => (s.id === shot.id ? { ...s, status: "ok" } : s)));
      // Success: purge the persisted copy from both IndexedDB and Mongo.
      deleteShot({ userId, shotId: shot.id });
      api.delete(`/v1/paycheck-stubs/pending-batch/${shot.id}`).catch(() => {});
      return { ok: true, stub: data?.stub };
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      setCapturedBlobs((prev) => prev.map((s) => (s.id === shot.id ? { ...s, status: "error", errorMessage: msg } : s)));
      return { ok: false };
    }
  };

  const shipBatch = async () => {
    if (!capturedBlobs.length || shipping) return;
    // Only ship shots that haven't succeeded yet — this makes shipBatch()
    // safely re-callable after a partial-failure batch (equivalent to
    // "retry all").
    const pending = capturedBlobs.filter((s) => s.status !== "ok");
    if (!pending.length) {
      setCameraOpen(false);
      return;
    }
    setShipping(true);
    const total = pending.length;
    setShipProgress({ current: 0, total });
    stopCamera();
    let ok = 0;
    const okStubs = [];
    for (let i = 0; i < pending.length; i += 1) {
      setShipProgress({ current: i + 1, total });
      const result = await uploadOneShot(pending[i], String(i + 1));
      if (result.ok) {
        ok += 1;
        if (result.stub) okStubs.push(result.stub);
      }
    }
    setShipping(false);
    setShipProgress({ current: 0, total: 0 });
    // Auto-close only when everything succeeded; keep the modal open so the
    // user can inspect + retry any failed thumbnails.
    if (ok === total) {
      capturedBlobs.forEach((s) => s.thumbUrl && URL.revokeObjectURL(s.thumbUrl));
      setCapturedBlobs([]);
      setCameraOpen(false);
      setMinimized(false);
      // Stash the just-parsed stubs so the Recent Batch drawer can render
      // per-employer / per-pay-period detail on tap.
      setRecentBatch({ stubs: okStubs, openedAt: Date.now() });
      const totalGross = okStubs.reduce((a, s) => a + Number(s?.gross_pay || 0), 0);
      const totalWithheld = okStubs.reduce((a, s) => a + Number(s?.federal_withholding || 0), 0);
      const currency = (okStubs[0]?.currency || "USD").toUpperCase();
      const sym = currency === "USD" ? "$" : currency === "EUR" ? "€" : currency === "GBP" ? "£" : `${currency} `;
      const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
      toast.success(`${ok} paystub${ok === 1 ? "" : "s"} ingested`, {
        description: `${sym}${fmt(totalGross)} gross · ${sym}${fmt(totalWithheld)} federal withheld · +${fmt((forecast?.projected_annual_liability || 0))} projected liability`,
        duration: 8000,
        action: {
          label: "View",
          onClick: () => setDrawerOpen(true),
        },
      });
    } else {
      toast.warning(`Batch partial · ${ok}/${total} shipped · tap any red thumbnail to retry`);
    }
  };

  const retryShot = async (id) => {
    if (shipping) return;
    const shot = capturedBlobs.find((s) => s.id === id);
    if (!shot) return;
    setShipping(true);
    setShipProgress({ current: 1, total: 1 });
    const result = await uploadOneShot(shot, "retry");
    setShipping(false);
    setShipProgress({ current: 0, total: 0 });
    if (result.ok) {
      toast.success("Retry succeeded · paystub ingested");
    }
  };

  // ---------- Native tap-to-focus + iOS Safari graceful fallback ----------
  const handleFocusTap = async (e) => {
    if (!streamRef.current || !cameraReady) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const ny = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const track = streamRef.current.getVideoTracks?.()[0];
    const reticle = { x: e.clientX - rect.left, y: e.clientY - rect.top, id: `f-${Date.now()}`, ok: null };
    setFocusReticle(reticle);
    let applied = false;
    let fallbackUsed = false;
    try {
      const caps = typeof track?.getCapabilities === "function" ? track.getCapabilities() : {};
      const advanced = [];
      if (Array.isArray(caps.focusMode) && (caps.focusMode.includes("single-shot") || caps.focusMode.includes("manual"))) {
        advanced.push({ focusMode: caps.focusMode.includes("single-shot") ? "single-shot" : "manual" });
      }
      if (caps.pointsOfInterest) {
        advanced.push({ pointsOfInterest: [{ x: nx, y: ny }] });
      }
      if (advanced.length && typeof track.applyConstraints === "function") {
        await track.applyConstraints({ advanced });
        applied = true;
      }
    } catch {
      applied = false;
    }
    // iOS Safari fallback — Safari doesn't advertise focusMode in getCapabilities
    // and rejects manual focus constraints, so we nudge the video element to
    // force an immediate stream rebuffer + auto-lens-refocus.  currentTime+=0
    // is a legacy no-op on MediaStream sources but harmless; the pause/play
    // sequence is what actually triggers Safari's underlying AVFoundation
    // pipeline to reacquire focus at the current subject distance.
    if (!applied) {
      const v = videoRef.current;
      if (v) {
        try {
          v.currentTime += 0; // per spec — no-op on MediaStream but explicit
          v.pause();
          await v.play().catch(() => {});
          fallbackUsed = true;
        } catch {
          fallbackUsed = false;
        }
      }
    }
    setFocusReticle({ ...reticle, ok: applied ? true : fallbackUsed ? "fallback" : false });
    // Auto-clear after 900ms so it doesn't obstruct the composition guide.
    setTimeout(() => {
      setFocusReticle((cur) => (cur?.id === reticle.id ? null : cur));
    }, 900);
  };

  // Ensure the camera track always dies with the component.
  useEffect(() => stopCamera, []);

  const odometer = forecast?.odometer || { ytd_withheld: 0, ytd_gross: 0, currency: "USD" };
  const rolled = useCountUp(odometer.ytd_withheld || 0, 1200);
  const grossRolled = useCountUp(odometer.ytd_gross || 0, 1200);
  const drift = forecast?.underpayment_drift ?? 0;
  const isUnder = drift > 0;
  const symbol = CURRENCY(odometer.currency);

  return (
    <div
      data-testid="paycheck-optimizer-node"
      className="rounded-2xl p-6 glass relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(30,15,60,0.75), rgba(15,23,42,0.85))",
        border: "1px solid rgba(168,85,247,0.35)",
        boxShadow: "0 20px 40px -20px rgba(168,85,247,0.35)",
      }}
    >
      {/* W-2 Ingest Portal — Path A · Safe Handoff Builder */}
      <W2IngestPortal />
      {/* Specialized Ingestion Chambers — Auto · Home · Capital */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" data-testid="specialized-vaults-grid">
        <SpecializedVault kind="auto" />
        <SpecializedVault kind="home" />
        <SpecializedVault kind="capital" />
      </div>
      {/* Cash Flash · Inbound cash POS ledger + SE tax odometer */}
      <CashFlashPortal />
      {/* Auto-resume banner — surfaces orphaned batches from previous sessions */}
      {resumePrompt?.shots?.length > 0 && (
        <div
          data-testid="paycheck-resume-banner"
          className="mb-4 rounded-xl p-3 flex flex-wrap items-center gap-3"
          style={{
            background: "linear-gradient(180deg, rgba(6,182,212,0.12), rgba(15,23,42,0.6))",
            border: "1px solid rgba(6,182,212,0.42)",
          }}
        >
          <div className="w-8 h-8 rounded-full bg-cyan-500/25 border border-cyan-400 flex items-center justify-center text-cyan-200 shrink-0">
            <RotateCw size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-cyan-300">
              Unshipped batch detected
            </div>
            <div className="text-white text-sm">
              You have {resumePrompt.shots.length} paystub{resumePrompt.shots.length === 1 ? "" : "s"} from your last session — resume?
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={dismissResume}
              data-testid="paycheck-resume-dismiss"
              className="text-slate-300 hover:text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded"
            >
              Discard
            </button>
            <button
              onClick={acceptResume}
              data-testid="paycheck-resume-accept"
              className="text-slate-950 text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded font-bold hover:-translate-y-px transition-transform"
              style={{
                background: "linear-gradient(135deg, #06B6D4, #a855f7)",
                boxShadow: "0 10px 24px -8px rgba(6,182,212,0.55)",
                letterSpacing: "0.14em",
              }}
            >
              Resume {resumePrompt.shots.length} shot{resumePrompt.shots.length === 1 ? "" : "s"} ↻
            </button>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-purple-300 mb-1 flex items-center gap-1.5">
            <Sparkles size={11} /> Paycheck Optimizer · Live
          </div>
          <div className="text-white text-xl font-bold tracking-tight">
            Real-time withholding trajectory
          </div>
          <div className="text-slate-400 text-xs mt-0.5">
            {forecast?.stub_count || 0} paystub{(forecast?.stub_count || 0) === 1 ? "" : "s"} on file · {symbol}
            {(forecast?.projected_annual_liability || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} projected annual liability
          </div>
        </div>
        <div
          data-testid="paycheck-drift-badge"
          data-drift={isUnder ? "under" : "on-track"}
          className={`shrink-0 rounded-lg px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] flex items-center gap-1.5 ${
            isUnder
              ? "bg-rose-500/15 text-rose-300 border border-rose-500/40"
              : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
          }`}
        >
          {isUnder ? <AlertTriangle size={11} /> : <ShieldCheck size={11} />}
          {isUnder ? `Under ${symbol}${Math.abs(drift).toFixed(0)}` : "On track"}
        </div>
      </div>

      {/* Odometer row */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div
          data-testid="paycheck-ytd-withheld-odometer"
          className="rounded-xl p-4"
          style={{ background: "rgba(15,23,42,0.6)", border: "1px solid rgba(168,85,247,0.25)" }}
        >
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">YTD Fed Withheld</div>
          <div className="text-white text-3xl font-mono font-bold tracking-tight mt-1">
            {symbol}{Number(rolled).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div
          className="rounded-xl p-4"
          style={{ background: "rgba(15,23,42,0.6)", border: "1px solid rgba(6,182,212,0.22)" }}
        >
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">YTD Gross</div>
          <div className="text-white text-3xl font-mono font-bold tracking-tight mt-1">
            {symbol}{Number(grossRolled).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* Trajectory */}
      <div className="rounded-xl p-3 mb-4" style={{ background: "rgba(15,23,42,0.6)", border: "1px solid rgba(168,85,247,0.2)" }}>
        <div className="flex items-center gap-4 text-[9px] font-mono uppercase tracking-widest mb-2">
          <div className="flex items-center gap-1.5 text-purple-300">
            <span className="w-3 h-0.5 bg-purple-400" /> Gross
          </div>
          <div className="flex items-center gap-1.5 text-yellow-300">
            <span className="w-3 h-0.5 border-t border-dashed border-yellow-400" /> Withheld
          </div>
          <div className="ml-auto flex items-center gap-1 text-slate-500">
            <TrendingUp size={10} /> {forecast?.trajectory?.length || 0}-period trajectory
          </div>
        </div>
        <TrajectorySVG trajectory={forecast?.trajectory || []} currencySymbol={symbol} />
      </div>

      {/* Upload dropzone */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          data-testid="paycheck-upload-input"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="paycheck-upload-btn"
          className={`flex-1 rounded-lg h-11 font-bold text-slate-950 text-sm uppercase tracking-widest transition-transform flex items-center justify-center gap-2 ${uploading ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
          style={{
            background: "linear-gradient(135deg, #a855f7, #06B6D4)",
            boxShadow: "0 12px 26px -10px rgba(168,85,247,0.65)",
            letterSpacing: "0.14em",
          }}
        >
          <Upload size={14} />
          {uploading ? "Ingesting…" : "Upload paystub"}
        </button>
        <button
          onClick={openCamera}
          disabled={uploading}
          data-testid="paycheck-camera-btn"
          className={`flex-1 sm:flex-none sm:min-w-[220px] rounded-lg h-11 font-bold text-white text-sm uppercase tracking-widest transition-transform flex items-center justify-center gap-2 ${uploading ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(30,15,60,0.85))",
            border: "1px solid rgba(168,85,247,0.55)",
            boxShadow: "inset 0 0 0 1px rgba(216,180,254,0.15), 0 12px 26px -14px rgba(168,85,247,0.55)",
            letterSpacing: "0.14em",
          }}
        >
          <Camera size={14} />
          Capture Stub via Camera
        </button>
        <div className="hidden lg:block text-[10px] font-mono uppercase tracking-widest text-slate-500 max-w-[180px] leading-snug">
          Auto-routes via keyword discriminator if you upload from Scan too.
        </div>
      </div>

      {/* Native camera viewport overlay — portalled to document.body so parent
          transforms in DashboardLayout can't trap position:fixed.  Hidden when
          minimized but still mounted (state preserved). */}
      {cameraOpen && !minimized && typeof document !== "undefined" && createPortal(
        <div
          data-testid="paycheck-camera-overlay"
          className="fixed inset-0 z-[9800] flex items-start sm:items-center justify-center px-3 py-4 overflow-y-auto"
          style={{ background: "rgba(2,6,23,0.94)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
          onClick={(e) => { if (e.target === e.currentTarget && !shipping) closeCamera(); }}
        >
          <div
            className="relative w-full max-w-xl rounded-2xl my-auto"
            style={{
              background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(30,15,60,0.98))",
              border: "1px solid rgba(168,85,247,0.5)",
              boxShadow: "0 30px 70px -20px rgba(168,85,247,0.55)",
            }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-purple-500/25 gap-2">
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-purple-300 flex items-center gap-1.5 truncate">
                <Aperture size={12} /> Native camera · paystub capture
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {/* Minimize — only relevant when a ship is in flight or shots
                    are queued; hides overlay so user can keep working while
                    upload continues in the background. */}
                {(shipping || capturedBlobs.length > 0) && (
                  <button
                    onClick={() => setMinimized(true)}
                    data-testid="paycheck-camera-minimize"
                    className="text-slate-400 hover:text-white p-1 rounded"
                    aria-label="Minimize camera"
                    title="Ship in background"
                  >
                    <Minimize2 size={16} />
                  </button>
                )}
                <button
                  onClick={closeCamera}
                  disabled={shipping}
                  data-testid="paycheck-camera-close"
                  className={`text-slate-400 hover:text-white p-1 rounded ${shipping ? "opacity-40 cursor-not-allowed" : ""}`}
                  aria-label="Close camera"
                  title={shipping ? "Ship in progress — use Minimize" : "Close"}
                >
                  <CloseX size={18} />
                </button>
              </div>
            </div>
            {/* Video area — capped max-height so the shutter row is always
                visible on short viewports without needing to scroll.  Aspect
                flips to 4:3 in landscape so wide paystubs frame naturally.
                Click/tap anywhere on the frame triggers hardware focus. */}
            <div
              data-testid="paycheck-camera-frame"
              data-orientation={isLandscape ? "landscape" : "portrait"}
              className="relative bg-black overflow-hidden cursor-crosshair"
              style={{
                aspectRatio: isLandscape ? "4 / 3" : "3 / 4",
                maxHeight: "calc(100vh - 280px)",
              }}
              onClick={handleFocusTap}
            >
              <video
                ref={videoRef}
                data-testid="paycheck-camera-video"
                playsInline
                muted
                autoPlay
                className="w-full h-full object-cover pointer-events-none"
                onLoadedMetadata={() => setCameraReady(true)}
              />
              {/* White flash overlay on each shot capture */}
              {flashKey > 0 && (
                <span
                  key={`flash-${flashKey}`}
                  data-testid="paycheck-camera-flash"
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-white"
                  style={{ animation: "paystub-cam-flash 320ms ease-out forwards" }}
                />
              )}
              {/* Tap-to-focus reticle */}
              {focusReticle && (
                <span
                  data-testid="paycheck-camera-focus-reticle"
                  data-focus-applied={
                    focusReticle.ok === true ? "true"
                    : focusReticle.ok === "fallback" ? "fallback"
                    : focusReticle.ok === false ? "false"
                    : "pending"
                  }
                  aria-hidden="true"
                  className="pointer-events-none absolute rounded-full"
                  style={{
                    width: 68, height: 68,
                    left: focusReticle.x - 34,
                    top: focusReticle.y - 34,
                    border: `2px solid ${
                      focusReticle.ok === false ? "rgba(244,114,182,0.9)"
                      : focusReticle.ok === "fallback" ? "rgba(253,224,71,0.95)"
                      : "rgba(103,232,249,0.95)"
                    }`,
                    boxShadow: `0 0 24px ${
                      focusReticle.ok === false ? "rgba(244,114,182,0.55)"
                      : focusReticle.ok === "fallback" ? "rgba(253,224,71,0.55)"
                      : "rgba(103,232,249,0.55)"
                    }`,
                    animation: "paystub-cam-reticle 900ms cubic-bezier(0.22,1,0.36,1) forwards",
                  }}
                />
              )}
              {!cameraReady && !cameraError && (
                <div
                  data-testid="paycheck-camera-connecting"
                  className="absolute inset-0 flex flex-col items-center justify-center text-purple-200 gap-2 pointer-events-none"
                >
                  <div
                    className="w-10 h-10 rounded-full border-2 border-purple-400/60 border-t-transparent"
                    style={{ animation: "paystub-cam-spin 1.1s linear infinite" }}
                  />
                  <div className="text-[10px] font-mono uppercase tracking-widest">Connecting to camera…</div>
                </div>
              )}
              {cameraError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-rose-200 gap-2 px-4 text-center pointer-events-none">
                  <AlertTriangle size={22} className="text-rose-300" />
                  <div className="text-sm font-semibold">{cameraError}</div>
                </div>
              )}
              {(uploading || shipping) && (
                <div
                  data-testid="paycheck-camera-uploading"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none"
                  style={{ background: "rgba(2,6,23,0.75)", backdropFilter: "blur(4px)" }}
                >
                  <div
                    className="w-16 h-16 rounded-full border-2 border-purple-400 border-t-transparent"
                    style={{ animation: "paystub-cam-spin 1.1s linear infinite" }}
                  />
                  <div className="text-white text-sm font-mono">
                    {shipping && shipProgress.total > 0
                      ? `Uploading ${shipProgress.current} of ${shipProgress.total}…`
                      : "Ingesting via GPT-5.2 Vision…"}
                  </div>
                  {shipping && shipProgress.total > 0 && (
                    <div
                      data-testid="paycheck-batch-progress-bar"
                      className="w-40 h-1.5 rounded-full overflow-hidden"
                      style={{ background: "rgba(148,163,184,0.2)" }}
                    >
                      <div
                        className="h-full transition-all duration-300"
                        style={{
                          width: `${(Math.max(0, shipProgress.current - 1) / shipProgress.total) * 100}%`,
                          background: "linear-gradient(90deg, #a855f7, #06B6D4)",
                        }}
                      />
                    </div>
                  )}
                  <div className="text-[10px] font-mono uppercase tracking-widest text-purple-300">
                    {symbol}{Number(rolled).toLocaleString(undefined, { maximumFractionDigits: 0 })} YTD · live
                  </div>
                </div>
              )}
              {/* Composition guides — wider inset on landscape */}
              {cameraReady && !uploading && !shipping && (
                <div
                  data-testid="paycheck-camera-guide"
                  className={`pointer-events-none absolute rounded-xl border border-purple-400/40 ${isLandscape ? "inset-x-10 inset-y-4" : "inset-4 sm:inset-6"}`}
                />
              )}
              {/* Batch shot-count chip — top-right corner of the video frame */}
              {capturedBlobs.length > 0 && (
                <div
                  data-testid="paycheck-batch-count-chip"
                  className="pointer-events-none absolute top-3 right-3 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] flex items-center gap-1.5"
                  style={{
                    background: "linear-gradient(135deg, rgba(168,85,247,0.85), rgba(6,182,212,0.85))",
                    color: "#0f172a",
                    boxShadow: "0 10px 24px -8px rgba(168,85,247,0.6)",
                  }}
                >
                  {capturedBlobs.length} shot{capturedBlobs.length === 1 ? "" : "s"} queued
                </div>
              )}
              {/* Tap-to-focus hint — first render only */}
              {cameraReady && !uploading && !shipping && !focusReticle && (
                <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-widest text-slate-300/70">
                  Tap frame to focus
                </div>
              )}
            </div>
            {/* Batch thumbnail strip — only visible when N > 0.  Each thumb
                shows its status: queued (purple ring), uploading (spinner),
                ok (green check), error (red ring + Retry button). */}
            {capturedBlobs.length > 0 && (
              <div
                data-testid="paycheck-batch-strip"
                className="flex gap-2 overflow-x-auto px-4 pt-3 pb-2"
                style={{ scrollbarWidth: "none" }}
              >
                {capturedBlobs.map((s, i) => {
                  const isError = s.status === "error";
                  const isOk = s.status === "ok";
                  const isUploading = s.status === "uploading";
                  const ringColor = isError ? "#EF4444" : isOk ? "#10B981" : "rgba(168,85,247,0.5)";
                  return (
                    <div
                      key={s.id}
                      data-testid={`paycheck-batch-thumb-${i}`}
                      data-status={s.status}
                      className={`relative shrink-0 rounded-md overflow-hidden ${isError ? "paystub-thumb-error-ring" : ""}`}
                      style={{
                        width: isError ? 92 : 56,
                        height: 56,
                        border: `${isError ? 2 : 1}px solid ${ringColor}`,
                        boxShadow: isError ? "0 0 0 2px rgba(239,68,68,0.2)" : "none",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <img src={s.thumbUrl} alt="" className="w-14 h-full object-cover shrink-0" />
                      {isUploading && (
                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ background: "rgba(2,6,23,0.5)" }}>
                          <span
                            className="w-5 h-5 rounded-full border-2 border-purple-300 border-t-transparent"
                            style={{ animation: "paystub-cam-spin 0.9s linear infinite" }}
                          />
                        </span>
                      )}
                      {isOk && (
                        <span className="absolute top-0.5 left-0.5 bg-emerald-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold" aria-hidden="true">✓</span>
                      )}
                      {isError && (
                        <button
                          onClick={() => retryShot(s.id)}
                          disabled={shipping}
                          data-testid={`paycheck-batch-retry-${i}`}
                          title={s.errorMessage || "Retry this shot"}
                          className={`absolute right-0 top-0 bottom-0 px-2 text-[10px] font-mono uppercase tracking-widest text-white flex items-center gap-1 ${shipping ? "opacity-60 cursor-wait" : "hover:brightness-110"}`}
                          style={{ background: "linear-gradient(135deg, #EF4444, #b91c1c)" }}
                        >
                          <RotateCw size={11} /> Retry
                        </button>
                      )}
                      {!isError && !isUploading && (
                        <button
                          onClick={() => discardShot(s.id)}
                          disabled={shipping}
                          data-testid={`paycheck-batch-discard-${i}`}
                          className="absolute top-0.5 right-0.5 bg-slate-900/85 text-white rounded-full w-4 h-4 flex items-center justify-center hover:bg-rose-500/70"
                          aria-label={`Discard shot ${i + 1}`}
                        >
                          <CloseX size={10} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Storage quota warning banner — non-blocking */}
            {quotaWarning && (
              <div
                data-testid="paycheck-camera-quota-warning"
                className="mx-4 mt-3 rounded-lg px-3 py-2 flex items-center gap-2"
                style={{
                  background: "rgba(234,179,8,0.12)",
                  border: "1px solid rgba(234,179,8,0.45)",
                }}
              >
                <HardDrive size={14} className="text-yellow-300 shrink-0" />
                <div className="text-[10px] font-mono uppercase tracking-widest text-yellow-200 leading-tight">
                  Low device storage · {(quotaWarning.free / 1024 / 1024).toFixed(0)}MB free · large batches may fail
                </div>
                <button
                  onClick={() => setQuotaWarning(null)}
                  data-testid="paycheck-quota-dismiss"
                  className="ml-auto text-yellow-300 hover:text-white p-0.5 shrink-0"
                  aria-label="Dismiss storage warning"
                >
                  <CloseX size={12} />
                </button>
              </div>
            )}
            {/* Shutter row — larger, centered, prominent circular capture
                button.  Ship-batch appears once at least one shot is queued. */}
            <div className="flex items-center justify-between gap-3 px-4 py-4">
              <button
                onClick={closeCamera}
                disabled={shipping}
                data-testid="paycheck-camera-cancel"
                className="text-slate-300 hover:text-white text-xs font-mono uppercase tracking-widest px-3 py-2 rounded shrink-0"
              >
                Cancel
              </button>
              <button
                onClick={snapToBatch}
                disabled={!cameraReady || shipping}
                data-testid="paycheck-camera-shutter"
                aria-label="Snap paystub"
                className={`shrink-0 rounded-full flex items-center justify-center transition-transform ${!cameraReady || shipping ? "opacity-60 cursor-not-allowed" : "hover:-translate-y-px active:scale-95"}`}
                style={{
                  width: 72, height: 72,
                  background: "radial-gradient(circle at 30% 30%, #ffffff 0%, #f3e8ff 45%, #a855f7 100%)",
                  boxShadow: "0 0 0 4px rgba(15,23,42,0.9), 0 0 0 6px rgba(168,85,247,0.85), 0 18px 40px -12px rgba(168,85,247,0.75)",
                }}
              >
                <div
                  className="rounded-full flex items-center justify-center text-slate-900"
                  style={{ width: 52, height: 52, background: "linear-gradient(135deg, #ffffff, #ede9fe)" }}
                >
                  <Aperture size={26} strokeWidth={2.25} />
                </div>
              </button>
              {capturedBlobs.length > 0 ? (
                <button
                  onClick={shipBatch}
                  disabled={shipping || !capturedBlobs.length}
                  data-testid="paycheck-camera-ship-batch"
                  className={`shrink-0 rounded-lg h-11 px-4 font-bold text-slate-950 text-xs uppercase tracking-widest flex items-center gap-2 transition-transform ${shipping ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
                  style={{
                    background: "linear-gradient(135deg, #a855f7, #06B6D4)",
                    boxShadow: "0 12px 26px -10px rgba(168,85,247,0.7)",
                    letterSpacing: "0.14em",
                  }}
                >
                  <span data-testid="paycheck-batch-progress-chip">
                    {shipping && shipProgress.total > 0
                      ? `Uploading ${shipProgress.current} of ${shipProgress.total}…`
                      : shipping ? "Sending…" : `Ship ${capturedBlobs.length}`}
                  </span>
                </button>
              ) : (
                <>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hidden sm:block max-w-[120px] text-right leading-snug shrink-0">
                    Frame the whole stub inside the outline
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 sm:hidden shrink-0">
                    {shipping ? "Sending…" : cameraReady ? "Tap to snap" : "Connecting…"}
                  </div>
                </>
              )}
            </div>
          </div>
          <style>{`
            @keyframes paystub-cam-spin { to { transform: rotate(360deg); } }
            @keyframes paystub-cam-flash {
              0% { opacity: 0.9; }
              100% { opacity: 0; }
            }
            @keyframes paystub-cam-reticle {
              0%   { transform: scale(1.9); opacity: 0; }
              25%  { transform: scale(1.05); opacity: 1; }
              80%  { transform: scale(1);    opacity: 1; }
              100% { transform: scale(1);    opacity: 0; }
            }
          `}</style>
        </div>,
        document.body,
      )}
      {/* Floating background progress tile — appears when the user minimizes
          the modal during an active batch ship.  Persistent, live-updating,
          tap to expand back to the modal. */}
      {cameraOpen && minimized && typeof document !== "undefined" && createPortal(
        <div
          data-testid="paycheck-batch-background-toast"
          className="fixed bottom-4 right-4 z-[9700] rounded-2xl overflow-hidden max-w-[340px]"
          style={{
            background: "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(30,15,60,0.96))",
            border: "1px solid rgba(168,85,247,0.55)",
            boxShadow: "0 24px 60px -18px rgba(168,85,247,0.55)",
          }}
        >
          <div className="flex items-center gap-3 px-4 py-3">
            {shipping ? (
              <div
                className="w-8 h-8 rounded-full border-2 border-purple-400 border-t-transparent shrink-0"
                style={{ animation: "paystub-cam-spin 1.1s linear infinite" }}
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-purple-500/25 border border-purple-400 flex items-center justify-center text-purple-200 shrink-0">
                {capturedBlobs.filter((s) => s.status === "error").length > 0 ? (
                  <AlertTriangle size={14} className="text-rose-300" />
                ) : (
                  <Aperture size={14} />
                )}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-purple-300 truncate">
                {shipping
                  ? `Uploading ${shipProgress.current} of ${shipProgress.total}…`
                  : capturedBlobs.filter((s) => s.status === "error").length > 0
                    ? `${capturedBlobs.filter((s) => s.status === "error").length} shot${capturedBlobs.filter((s) => s.status === "error").length === 1 ? "" : "s"} failed · retry required`
                    : `${capturedBlobs.length} shot${capturedBlobs.length === 1 ? "" : "s"} queued`}
              </div>
              {shipping && shipProgress.total > 0 && (
                <div className="w-full h-1 rounded-full overflow-hidden mt-1.5" style={{ background: "rgba(148,163,184,0.2)" }}>
                  <div
                    data-testid="paycheck-batch-background-bar"
                    className="h-full transition-all duration-300"
                    style={{
                      width: `${(Math.max(0, shipProgress.current - 1) / shipProgress.total) * 100}%`,
                      background: "linear-gradient(90deg, #a855f7, #06B6D4)",
                    }}
                  />
                </div>
              )}
            </div>
            <button
              onClick={() => setMinimized(false)}
              data-testid="paycheck-batch-background-expand"
              className="text-slate-300 hover:text-white p-1 rounded shrink-0"
              aria-label="Expand camera modal"
              title="Expand"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        </div>,
        document.body,
      )}
      {/* Recent Batch drawer — deep-linked from the analytics toast */}
      {drawerOpen && recentBatch?.stubs && typeof document !== "undefined" && createPortal(
        <div
          data-testid="paycheck-recent-batch-drawer"
          className="fixed inset-0 z-[9750]"
          onClick={(e) => { if (e.target === e.currentTarget) setDrawerOpen(false); }}
          style={{ background: "rgba(2,6,23,0.55)", backdropFilter: "blur(4px)" }}
        >
          <aside
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] overflow-y-auto"
            style={{
              background: "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(30,15,60,0.96))",
              borderLeft: "1px solid rgba(168,85,247,0.45)",
              boxShadow: "-30px 0 60px -20px rgba(168,85,247,0.35)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              animation: "paystub-drawer-slide 320ms cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-purple-500/25">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-purple-300 flex items-center gap-1.5">
                  <Sparkles size={11} /> Recent Batch
                </div>
                <div className="text-white text-lg font-bold mt-0.5">
                  {recentBatch.stubs.length} paystub{recentBatch.stubs.length === 1 ? "" : "s"} · just ingested
                </div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                data-testid="paycheck-drawer-close"
                className="text-slate-400 hover:text-white p-1 rounded"
                aria-label="Close drawer"
              >
                <CloseX size={18} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {recentBatch.stubs.map((s, i) => {
                const cur = (s?.currency || "USD").toUpperCase();
                const sym = cur === "USD" ? "$" : cur === "EUR" ? "€" : cur === "GBP" ? "£" : `${cur} `;
                return (
                  <div
                    key={s?.id || i}
                    data-testid={`paycheck-drawer-stub-${i}`}
                    className="rounded-xl p-4"
                    style={{
                      background: "rgba(15,23,42,0.6)",
                      border: "1px solid rgba(168,85,247,0.28)",
                    }}
                  >
                    <div className="flex items-center gap-2 text-white text-sm font-semibold">
                      <Building2 size={14} className="text-purple-300 shrink-0" />
                      <span className="truncate">{s?.employer || "Unknown Employer"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400 text-[11px] font-mono mt-1">
                      <Calendar size={11} className="shrink-0" />
                      {s?.pay_period_start || "—"} → {s?.pay_period_end || "—"}
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Gross</div>
                        <div className="text-white text-lg font-mono font-bold">
                          {sym}{Number(s?.gross_pay || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div>
                        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Fed Withheld</div>
                        <div className="text-yellow-300 text-lg font-mono font-bold">
                          {sym}{Number(s?.federal_withholding || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <style>{`
              @keyframes paystub-drawer-slide {
                from { transform: translateX(100%); }
                to   { transform: translateX(0); }
              }
            `}</style>
          </aside>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default PaycheckOptimizerNode;

// Cross-Vault YoY Rollup — mounts inside the Cash Flash Portal. Fans out to
// the three vault list endpoints, filters each row by year, and computes:
//   Auto Line 9   = sum(total) × biz-use%
//   Capital L13   = sum(depreciation_schedule[0].deduction)
//   Home L25      = sum(deductible_amount)
// Three amber bars proportional to (auto + home + capital) per TY.
function CrossVaultYoYRollup() {
  const [state, setState] = useState({ ready: false, rows: null, biz: 1 });
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/v1/ingest/vault/auto?limit=200").catch(() => null),
      api.get("/v1/ingest/vault/home?limit=200").catch(() => null),
      api.get("/v1/ingest/vault/capital?limit=200").catch(() => null),
      api.get("/mileage").catch(() => null),
    ]).then(([a, h, c, m]) => {
      if (cancelled) return;
      const rows = {
        auto:    a?.data?.docs || a?.data?.documents || [],
        home:    h?.data?.docs || h?.data?.documents || [],
        capital: c?.data?.docs || c?.data?.documents || [],
      };
      const total = Number(m?.data?.total_miles || 0);
      const bizMi = Number(m?.data?.business_miles || 0);
      const biz = total > 0 ? Math.min(1, Math.max(0, bizMi / total)) : 1;
      setState({ ready: true, rows, biz });
    });
    return () => { cancelled = true; };
  }, []);
  if (!state.ready || !state.rows) return null;
  const inYear = (r, y) => {
    const s = String(r?.date || r?.uploaded_at || "");
    return s.startsWith(`${y}-`) || s.startsWith(`${y}/`);
  };
  const YEARS = [2024, 2025, 2026];
  const computed = YEARS.map((y) => {
    const a = state.rows.auto.filter((r) => inYear(r, y))
      .reduce((s, r) => s + Number(r.total || 0), 0) * state.biz;
    const h = state.rows.home.filter((r) => inYear(r, y))
      .reduce((s, r) => s + Number(r.deductible_amount || 0), 0);
    const c = state.rows.capital.filter((r) => inYear(r, y))
      .reduce((s, r) => {
        if (r.section_179_elected) return s + Number(r.purchase_price || 0);
        return s + Number(r.depreciation_schedule?.[0]?.deduction || 0);
      }, 0);
    return { year: y, auto: a, home: h, capital: c, total: a + h + c };
  });
  const grandMax = Math.max(1, ...computed.map((c) => c.total));
  const money = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();
  const isEmpty = computed.every((c) => c.total === 0);
  return (
    <div
      className="mt-4 rounded-xl p-4 border border-amber-500/40 bg-slate-950/40"
      data-testid="cash-flash-yoy-rollup"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-mono uppercase tracking-[0.28em]" style={{ color: "#FFB000" }}>
          📊 Cross-Vault YoY Rollup · Business Deductions
        </div>
        <span className="text-[8px] font-mono uppercase tracking-widest text-amber-200/60">
          Auto §162 + Home §280A + Capital §168
        </span>
      </div>
      {isEmpty ? (
        <div className="rounded-lg py-4 text-center text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-dashed border-amber-500/20">
          No vault deductions logged yet · start photographing receipts to see year-over-year contrast
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {computed.map((row) => {
            const isPeak = row.total > 0 && row.total === grandMax;
            const barH = Math.max(6, Math.round((row.total / grandMax) * 120));
            const seg = (v, label, hue) => {
              const h = row.total ? Math.round((v / row.total) * barH) : 0;
              return (
                <div key={label} className="w-full" style={{ height: `${h}px`, background: hue, opacity: h ? 1 : 0 }} title={`${label} · ${money(v)}`} />
              );
            };
            return (
              <div
                key={row.year}
                data-testid={`cash-flash-yoy-bar-${row.year}`}
                data-peak={isPeak ? "true" : "false"}
                className="rounded-lg p-3 border flex flex-col items-center gap-2"
                style={{
                  borderColor: isPeak ? "rgba(255,176,0,0.75)" : "rgba(148,163,184,0.15)",
                  background: isPeak ? "rgba(255,176,0,0.06)" : "rgba(255,255,255,0.02)",
                  boxShadow: isPeak ? "0 0 12px rgba(255,176,0,0.45)" : undefined,
                }}
              >
                <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: isPeak ? "#FFB000" : "#94a3b8" }}>
                  TY {row.year}
                </span>
                <div className="flex flex-col-reverse w-8 rounded-md overflow-hidden bg-slate-900" style={{ height: "128px" }}>
                  {seg(row.auto,    "auto",    "#FF6B00")}
                  {seg(row.home,    "home",    "#06B6D4")}
                  {seg(row.capital, "capital", "#FFD700")}
                </div>
                <span className="text-[11px] font-mono font-bold tabular-nums"
                      style={{ color: isPeak ? "#FFB000" : "#e2e8f0", textShadow: isPeak ? "0 0 8px rgba(255,176,0,0.8)" : undefined }}>
                  {money(row.total)}
                </span>
                <div className="flex flex-col gap-0.5 text-[8px] font-mono tracking-widest">
                  <span style={{ color: "#FF6B00" }}>AUTO {money(row.auto)}</span>
                  <span style={{ color: "#06B6D4" }}>HOME {money(row.home)}</span>
                  <span style={{ color: "#FFD700" }}>CAP  {money(row.capital)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Cash Flash · Inbound POS ledger + SE tax odometer
// ============================================================================
export function CashFlashPortal() {
  const [amountStr, setAmountStr] = useState("");
  const [nameStr, setNameStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({
    count: 0,
    gross_cash_cents: 0,
    self_employment_tax_cents: 0,
    federal_marginal_tax_cents: 0,
    total_tax_cents: 0,
    user_net_retention_cents: 0,
    statutory_tax_buffer_reserves_cents: 0,
  });
  const [lastEntry, setLastEntry] = useState(null);
  const inputRef = useRef(null);
  const nameRef = useRef(null);
  // Ledger Purge Console — dual-action wipe (state + backend rows).
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const performLedgerReset = async () => {
    if (resetBusy) return;
    setResetBusy(true);
    const toastId = toast.loading("♻️ Purging Cash Flash ledger…");
    try {
      await api.delete("/v1/ingest/cash-flash").catch(() => null);
      setEntries([]);
      setLastEntry(null);
      setAmountStr("");
      setNameStr("");
      setTotals({
        count: 0,
        gross_cash_cents: 0,
        self_employment_tax_cents: 0,
        federal_marginal_tax_cents: 0,
        total_tax_cents: 0,
        user_net_retention_cents: 0,
        statutory_tax_buffer_reserves_cents: 0,
      });
      toast.success("Ledger purged · odometers back to $0.00", { id: toastId });
      window.dispatchEvent(new CustomEvent("reset-audit-log-updated"));
      setResetOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed", { id: toastId });
    } finally {
      setResetBusy(false);
    }
  };

  // Hydrate rolling totals on mount so the odometer isn't stuck at zero for
  // returning users. Every accumulator field is derived DYNAMICALLY against
  // the true `cash_amount_pennies` of each row — legacy rows without a
  // backend `statutory_tax_buffer_reserves_cents` field are recomputed
  // client-side using the same 4-step statutory formula so Rolling Gross ·
  // Keep · Buffer stay in perfect 0-cent sync regardless of row vintage.
  useEffect(() => {
    let cancelled = false;
    const deriveBufferCents = (grossCents) => {
      const g = Number(grossCents || 0) / 100;
      const base = g * 0.9235;
      const ss  = Math.min(base, 184500) * 0.124;
      const med = base * 0.029;
      const sur = Math.max(0, base - 200000) * 0.009;
      return Math.round((ss + med + sur) * 100);
    };
    api.get("/v1/ingest/cash-flash?limit=200")
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.entries || [];
        setEntries(rows);
        const agg = rows.reduce((a, e) => {
          const gross = Number(e.cash_amount_pennies || 0);
          const sct = e.system_calculated_tax || {};
          // Prefer backend numbers; fall back to live derivation for legacy rows.
          const buffer = sct.statutory_tax_buffer_reserves_cents != null
            ? Number(sct.statutory_tax_buffer_reserves_cents)
            : deriveBufferCents(gross);
          const keep = Math.max(0, gross - buffer);
          a.count += 1;
          a.gross_cash_cents += gross;
          a.self_employment_tax_cents += Number(sct.self_employment_tax_cents || 0);
          a.federal_marginal_tax_cents += Number(sct.federal_marginal_tax_cents || 0);
          a.total_tax_cents += buffer;
          a.user_net_retention_cents += keep;
          a.statutory_tax_buffer_reserves_cents += buffer;
          return a;
        }, { count: 0, gross_cash_cents: 0, self_employment_tax_cents: 0, federal_marginal_tax_cents: 0, total_tax_cents: 0, user_net_retention_cents: 0, statutory_tax_buffer_reserves_cents: 0 });
        setTotals(agg);
      })
      .catch(() => { /* silent; empty state is fine */ });
    return () => { cancelled = true; };
  }, []);

  const grossRoll = useCountUp(totals.gross_cash_cents / 100, 1200);
  const keepRoll  = useCountUp(totals.user_net_retention_cents / 100, 1200);
  const bufferRoll = useCountUp(totals.statutory_tax_buffer_reserves_cents / 100, 1200);

  const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const submit = async (e) => {
    e?.preventDefault?.();
    const parsed = Number(String(amountStr).replace(/[^0-9.]/g, ""));
    if (!parsed || parsed <= 0) {
      toast.error("Enter a positive cash amount");
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/v1/ingest/cash-flash", {
        cash_amount: parsed,
        sale_name: (nameStr || "").trim() || undefined,
      });
      setLastEntry(data.entry);
      setTotals(data.totals);
      setEntries((prev) => [data.entry, ...prev]);
      setAmountStr("");
      setNameStr("");
      const cut = money((data.entry?.system_calculated_tax?.total_tax_cents || 0) / 100);
      const keep = money((data.entry?.system_calculated_tax?.user_net_retention_cents || 0) / 100);
      toast.success(`Cash Flash logged · ${money(parsed)} in`, {
        description: `You keep ${keep} · Buffer ${cut} for IRS (IRC §61 + §1401).`,
      });
      inputRef.current?.focus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cash Flash failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="cash-flash-portal"
      className="relative rounded-2xl p-5 mb-5 cash-flash-halo"
      style={{
        background: "linear-gradient(180deg, rgba(30,20,4,0.85), rgba(15,23,42,0.9))",
        border: "1px solid rgba(255,176,0,0.55)",
      }}
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-[11px] font-mono uppercase tracking-[0.28em]" style={{ color: "#FFB000" }}>
          ⚡ Cash Flash Inbound Portal
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setResetOpen(true)}
            data-testid="cash-flash-reset-btn"
            className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:text-white hover:bg-rose-500/25 transition-colors"
            title="Wipe every cash sale, roll every odometer back to $0, and flush the backend ledger"
          >
            <RotateCcw size={11} /> ♻️ Reset Ledger
          </button>
          <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border border-amber-400/40 bg-amber-500/10 text-amber-200">
            IRC §1401 · SE Auto-Withhold
          </span>
        </div>
      </div>

      <form onSubmit={submit} className="flex items-stretch gap-2 mb-4 flex-wrap">
        <div className="relative w-full md:w-52">
          <input
            ref={nameRef}
            type="text"
            autoComplete="off"
            value={nameStr}
            onChange={(e) => setNameStr(e.target.value)}
            placeholder="🏷 Name this sale …"
            data-testid="cash-flash-name-input"
            disabled={busy}
            maxLength={80}
            className="w-full h-12 px-3 rounded-xl bg-slate-950/70 border border-amber-500/25 focus:border-amber-400/80 focus:outline-none text-white font-mono text-sm placeholder:text-slate-500"
            style={{ boxShadow: "inset 0 0 0 1px rgba(255,176,0,0.08)" }}
          />
        </div>
        <div className="flex-1 min-w-[180px] relative">
          <span
            className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-mono font-bold"
            style={{ color: "#FFB000", textShadow: "0 0 10px rgba(255,176,0,0.55)" }}
          >
            $
          </span>
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="Log an inbound cash sale …"
            data-testid="cash-flash-input"
            disabled={busy}
            className="w-full h-12 pl-8 pr-3 rounded-xl bg-slate-950/70 border border-amber-500/25 focus:border-amber-400/80 focus:outline-none text-white font-mono text-lg placeholder:text-slate-600"
            style={{ boxShadow: "inset 0 0 0 1px rgba(255,176,0,0.08)" }}
          />
        </div>
        <button
          type="submit"
          disabled={busy || !amountStr}
          data-testid="cash-flash-submit"
          className="h-12 px-5 rounded-xl font-bold text-xs uppercase tracking-widest text-slate-950 disabled:opacity-50"
          style={{
            background: "linear-gradient(135deg, #FFB000, #FFD700)",
            boxShadow: "0 10px 26px -10px rgba(255,176,0,0.75), inset 0 0 0 1px rgba(255,255,255,0.18)",
          }}
        >
          {busy ? "Stamping…" : "Stamp Cash"}
        </button>
      </form>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="rounded-lg p-3 border border-amber-500/30 bg-slate-950/50" data-testid="cash-flash-gross-odometer">
          <div className="text-[9px] uppercase tracking-widest text-amber-200/70">Rolling gross cash · IRC §61</div>
          <div className="text-xl font-mono font-bold text-white mt-1">{money(grossRoll)}</div>
        </div>
        <div className="rounded-lg p-3 border border-emerald-500/40 bg-slate-950/50" data-testid="cash-flash-keep-odometer">
          <div className="text-[9px] uppercase tracking-widest text-emerald-200/80">Your Money to Keep</div>
          <div
            className="text-xl font-mono font-bold text-emerald-300 mt-1"
            style={{ textShadow: "0 0 12px rgba(16,185,129,0.65)" }}
          >
            {money(keepRoll)}
          </div>
        </div>
        <div className="rounded-lg p-3 border border-amber-500/60 bg-slate-950/50" data-testid="cash-flash-buffer-odometer">
          <div className="text-[9px] uppercase tracking-widest text-amber-200/90">Statutory Tax Buffer Reserves</div>
          <div
            className="text-xl font-mono font-bold text-amber-300 mt-1"
            style={{ textShadow: "0 0 12px rgba(255,176,0,0.65)" }}
          >
            {money(bufferRoll)}
          </div>
        </div>
      </div>

      {lastEntry && (
        <div
          data-testid="cash-flash-last-entry"
          className="rounded-lg p-3 border border-amber-500/40 bg-slate-950/70 text-[11px] font-mono flex flex-wrap items-center gap-x-4 gap-y-1"
        >
          <span className="text-amber-200">Last invoice</span>
          <span className="text-white">{lastEntry.sale_id}</span>
          <span className="text-slate-400">·</span>
          <span className="text-white">{money(lastEntry.cash_amount_pennies / 100)} in</span>
          <span className="text-slate-400">·</span>
          <span className="text-amber-300">
            SE {money((lastEntry.system_calculated_tax?.self_employment_tax_cents || 0) / 100)}
          </span>
          <span className="text-slate-400">·</span>
          <span className="text-cyan-300">
            Fed marg {Math.round(100 * (lastEntry.system_calculated_tax?.marginal_rate || 0))}%
          </span>
        </div>
      )}

      {/* Cross-Vault YoY Rollup — sums business deductions across Auto, Home,
          and Capital vaults for 2024/2025/2026 so managers can see how the
          write-off engine is trending year over year. Client-side aggregate
          only — no new endpoints. */}
      <CrossVaultYoYRollup />

      {/* Inbound Sales Ledger Vault — per-transaction glass rows */}
      <div className="mt-4" data-testid="cash-flash-ledger-vault">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-mono uppercase tracking-[0.28em]" style={{ color: "#FFB000" }}>
            📒 Inbound Sales Ledger Vault
          </div>
          <span className="text-[9px] font-mono uppercase tracking-widest text-amber-200/70">
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </span>
        </div>
        {entries.length === 0 ? (
          <div className="rounded-xl p-6 text-center text-[11px] font-mono uppercase tracking-widest text-slate-500 border border-dashed border-amber-500/25 bg-slate-950/40">
            No cash stamps yet · your first sale will materialize here
          </div>
        ) : (
          <ul className="space-y-2 max-h-[380px] overflow-y-auto pr-1 cash-flash-scroll" data-testid="cash-flash-ledger-list">
            {entries.map((e) => {
              const gross  = Number(e.cash_amount_pennies || 0) / 100;
              // Live derivation against the TRUE gross so legacy rows (missing
              // statutory_tax_buffer_reserves_cents / user_net_retention_cents)
              // never fall through to $0 or the legacy 22% marginal baseline.
              // Zero-cent drift is guaranteed by construction: buffer + keep
              // = gross to the last penny (see backend compute_cash_flash_
              // statutory · Buffer = SS + Med + HE surtax on 92.35% base).
              const grossCents = Number(e.cash_amount_pennies || 0);
              const bufferBackendCents = e.system_calculated_tax?.statutory_tax_buffer_reserves_cents;
              let bufferCents = bufferBackendCents;
              if (bufferCents == null) {
                const g = grossCents / 100;
                const base = g * 0.9235;
                const ss  = Math.min(base, 184500) * 0.124;
                const med = base * 0.029;
                const sur = Math.max(0, base - 200000) * 0.009;
                bufferCents = Math.round((ss + med + sur) * 100);
              }
              const keepCents = Math.max(0, grossCents - Number(bufferCents || 0));
              const keep   = keepCents / 100;
              const buffer = Number(bufferCents || 0) / 100;
              const ts     = e.invoice_stamped_at ? new Date(e.invoice_stamped_at) : null;
              return (
                <li
                  key={e.sale_id}
                  data-testid={`cash-flash-row-${e.sale_id}`}
                  className="cash-flash-row relative rounded-xl p-3 border border-amber-500/25"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    WebkitBackdropFilter: "blur(16px) saturate(140%)",
                    backdropFilter:       "blur(16px) saturate(140%)",
                    boxShadow: "inset 0 0 0 1px rgba(255,176,0,0.08)",
                  }}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-semibold text-sm truncate">
                          {e.sale_name || "Untitled cash sale"}
                        </span>
                        <span
                          className="inline-flex items-center gap-1 text-[8.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded audit-safe-glow"
                          style={{
                            background: "rgba(16,185,129,0.12)",
                            border: "1px solid rgba(16,185,129,0.55)",
                            color: "#6ee7b7",
                          }}
                          title="Anchored to IRC § 61 (Gross Income Defined)"
                        >
                          <ShieldCheck size={9} /> Audit-Safe · IRC §61
                        </span>
                      </div>
                      <div className="text-[10px] font-mono text-slate-500 mt-1">
                        <span className="text-amber-200/70">{e.sale_id}</span>
                        <span className="text-slate-600 mx-1.5">·</span>
                        {ts ? ts.toLocaleString(undefined, {
                          year: "numeric", month: "short", day: "2-digit",
                          hour: "2-digit", minute: "2-digit",
                        }) : "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                        Gross <span className="text-white ml-1">{money(gross)}</span>
                      </span>
                      <span
                        className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded"
                        style={{
                          background: "rgba(16,185,129,0.10)",
                          border: "1px solid rgba(16,185,129,0.45)",
                          color: "#6ee7b7",
                          textShadow: "0 0 8px rgba(16,185,129,0.55)",
                        }}
                      >
                        Keep {money(keep)}
                      </span>
                      <span
                        className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded"
                        style={{
                          background: "rgba(255,176,0,0.10)",
                          border: "1px solid rgba(255,176,0,0.55)",
                          color: "#fcd34d",
                          textShadow: "0 0 8px rgba(255,176,0,0.55)",
                        }}
                      >
                        Buffer {money(buffer)}
                      </span>
                      <StatutoryBreakdownChip entry={e} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <QuarterlyEstimatedReminder totals={totals} />

      <style>{`
        @keyframes cash-flash-halo-breathe {
          0%, 100% { box-shadow: 0 0 0 1px rgba(255,176,0,0.30), 0 0 18px rgba(255,176,0,0.28), 0 0 28px rgba(255,215,0,0.22); }
          50%      { box-shadow: 0 0 0 2px rgba(255,176,0,0.65), 0 0 34px rgba(255,176,0,0.65), 0 0 56px rgba(255,215,0,0.55); }
        }
        .cash-flash-halo { animation: cash-flash-halo-breathe 4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .cash-flash-halo { animation: none !important; } }

        /* Expanded-row layer override — when the Σ statutory breakdown panel
           is open, promote its parent row above every sibling row (and any
           subsequent viewport component further down the canvas) so the
           4-STEP STATUTORY BUFFER blueprint paints cleanly on top without
           visual clipping. Uses :has() selector (all evergreen browsers). */
        .cash-flash-row:has([aria-expanded="true"]) {
          position: relative;
          z-index: 50;
          box-shadow:
            0 25px 50px -12px rgba(0, 0, 0, 0.85),
            0 0 24px rgba(255, 176, 0, 0.35),
            inset 0 0 0 1px rgba(255, 176, 0, 0.25);
          border-color: rgba(255, 176, 0, 0.55);
        }
        /* Unclip the scroll container while ANY row's panel is open so the
           dropdown escapes overflow-y-auto and stays fully readable. */
        .cash-flash-scroll:has([aria-expanded="true"]) {
          overflow: visible;
        }
      `}</style>
      <ConfirmResetDialog
        open={resetOpen}
        title="Purge Cash Flash Ledger?"
        body="⚠️ Are you sure you want to permanently clear this data ledger? This will delete every cash sale row, roll the gross · keep · buffer odometers back to $0.00, and flush the backend ledger. This action cannot be undone."
        confirmLabel="♻️ Purge Ledger"
        busy={resetBusy}
        onCancel={() => setResetOpen(false)}
        onConfirm={performLedgerReset}
        testid="cash-flash-reset"
      />
    </div>
  );
}


// Statutory Breakdown Chip — click / hover to reveal the 4-step math that
// built this row's buffer. All four cents fields come straight from the
// backend `compute_cash_flash_statutory` payload (RS-CASHFLASH-STATUTORY-
// 2026-09-07-OK). Falls back to on-the-fly recomputation for legacy rows
// that predate the statutory rewrite.
function StatutoryBreakdownChip({ entry }) {
  const [open, setOpen] = useState(false);
  const s = entry?.system_calculated_tax || {};
  const gross = Number(entry?.cash_amount_pennies || 0);
  // Legacy rows only carry `self_employment_tax_cents` + `federal_marginal_tax_cents`
  // and no per-step split. Recompute the 4 steps client-side so the chip
  // still renders coherent numbers for archival ledger rows.
  const missing = s.taxable_base_cents == null || s.social_security_tax_cents == null;
  const derived = (() => {
    if (!missing) return null;
    const g = gross / 100;
    const base = g * 0.9235;
    const ss = Math.min(base, 184500) * 0.124;
    const med = base * 0.029;
    const sur = Math.max(0, base - 200000) * 0.009;
    const c = (v) => Math.round(v * 100);
    return {
      taxable_base_cents: c(base),
      social_security_tax_cents: c(ss),
      medicare_base_tax_cents: c(med),
      high_earner_surtax_cents: c(sur),
      _derived: true,
    };
  })();
  const step = missing ? derived : s;
  const money = (cents) => "$" + (Number(cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const buffer = Number(s.statutory_tax_buffer_reserves_cents
    || (step?.social_security_tax_cents + step?.medicare_base_tax_cents + step?.high_earner_surtax_cents)
    || 0);
  const testid = `cash-flash-breakdown-${entry?.sale_id || "row"}`;
  return (
    <span className="relative inline-block" onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        data-testid={testid}
        aria-expanded={open}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-mono font-bold transition"
        style={{
          background: "rgba(255,176,0,0.14)",
          border: "1px solid rgba(255,176,0,0.55)",
          color: "#fcd34d",
          textShadow: "0 0 6px rgba(255,176,0,0.55)",
          boxShadow: open ? "0 0 10px rgba(255,176,0,0.55)" : undefined,
          cursor: "pointer",
        }}
        title="Show statutory breakdown"
      >
        Σ
      </button>
      {open && (
        <div
          role="dialog"
          data-testid={`${testid}-panel`}
          className="absolute right-0 top-full mt-1 z-40 w-[300px] rounded-xl p-3 text-[10px] font-mono"
          style={{
            background: "linear-gradient(180deg, rgba(6,25,55,0.95), rgba(15,23,42,0.97))",
            border: "1px solid rgba(255,176,0,0.60)",
            boxShadow: "0 0 24px rgba(255,176,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.03)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            color: "#e2e8f0",
          }}
        >
          <div className="text-[9px] uppercase tracking-[0.28em] mb-2" style={{ color: "#fcd34d" }}>
            4-Step Statutory Buffer · IRC §1401 / §3101(b)(2)
          </div>
          <div className="grid grid-cols-[130px_1fr] gap-y-1.5 gap-x-2">
            <span className="text-slate-400">Gross</span>
            <span className="text-white tabular-nums text-right" data-testid={`${testid}-gross`}>{money(gross)}</span>
            <span className="text-slate-400">Step 1 · Taxable Base</span>
            <span className="text-slate-100 tabular-nums text-right" data-testid={`${testid}-step1`}>{money(step?.taxable_base_cents)}
              <span className="text-slate-500 ml-1">× 0.9235</span>
            </span>
            <span className="text-slate-400">Step 2 · Social Sec.</span>
            <span className="text-slate-100 tabular-nums text-right" data-testid={`${testid}-step2`}>{money(step?.social_security_tax_cents)}
              <span className="text-slate-500 ml-1">12.4% × ≤$184,500</span>
            </span>
            <span className="text-slate-400">Step 3 · Medicare</span>
            <span className="text-slate-100 tabular-nums text-right" data-testid={`${testid}-step3`}>{money(step?.medicare_base_tax_cents)}
              <span className="text-slate-500 ml-1">2.9% × base</span>
            </span>
            <span className="text-slate-400">Step 4 · HE Surtax</span>
            <span className="text-slate-100 tabular-nums text-right" data-testid={`${testid}-step4`}>{money(step?.high_earner_surtax_cents)}
              <span className="text-slate-500 ml-1">0.9% × over $200k</span>
            </span>
          </div>
          <div className="mt-2 pt-2 border-t border-amber-500/25 flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-widest text-slate-500">Buffer Reserve</span>
            <span className="text-[12px] font-bold tabular-nums" style={{ color: "#fcd34d", textShadow: "0 0 8px rgba(255,176,0,0.55)" }}>
              {money(buffer)}
            </span>
          </div>
          {missing && (
            <div className="mt-2 text-[8px] uppercase tracking-widest text-amber-200/70" data-testid={`${testid}-legacy-flag`}>
              · legacy row · client-side recomputation
            </div>
          )}
        </div>
      )}
    </span>
  );
}

// Quarterly Estimated Reminder — schedules a native browser notification for
// the next IRS quarterly deadline (Jan 15 / Apr 15 / Jun 15 / Sep 15). Only
// fires when the accumulated buffer is greater than zero. Persists the last
// dispatched deadline in localStorage so we don't spam the user on refresh.
function QuarterlyEstimatedReminder({ totals }) {
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported",
  );
  const bufferCents = Number(totals?.statutory_tax_buffer_reserves_cents || 0);
  const bufferDollars = bufferCents / 100;
  // Next upcoming IRS estimated-tax deadline. Deadlines fall on Apr 15 for Q1,
  // Jun 15 for Q2, Sep 15 for Q3, and Jan 15 (of the FOLLOWING year) for Q4.
  const nextDeadline = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const cands = [
      new Date(y, 0, 15),   // Jan 15 (Q4 of previous year)
      new Date(y, 3, 15),   // Apr 15 (Q1)
      new Date(y, 5, 15),   // Jun 15 (Q2)
      new Date(y, 8, 15),   // Sep 15 (Q3)
      new Date(y + 1, 0, 15), // Jan 15 next year (Q4)
    ];
    return cands.find((d) => d > now) || cands[cands.length - 1];
  })();
  const quarterLabel = (() => {
    const m = nextDeadline.getMonth();
    if (m === 0) return "Q4";
    if (m === 3) return "Q1";
    if (m === 5) return "Q2";
    return "Q3";
  })();
  const daysUntil = Math.ceil((nextDeadline - new Date()) / 86400000);
  useEffect(() => {
    if (bufferCents <= 0) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    // A single scheduled reminder per (deadline, buffer bucket) — 24 hours
    // ahead of the deadline. localStorage guards against duplicate dispatch.
    const dispatchKey = `rs.cash_flash.reminder.${nextDeadline.toISOString().slice(0, 10)}`;
    if (localStorage.getItem(dispatchKey)) return;
    const msLead = 24 * 60 * 60 * 1000;
    const fireAt = nextDeadline.getTime() - msLead;
    const delay = Math.max(0, fireAt - Date.now());
    // If deadline is >30 days away, wait until the browser next tick — do
    // not schedule long timers that a Chrome throttle could drop silently.
    if (delay > 30 * 86400000) return;
    const id = setTimeout(() => {
      try {
        new Notification("💰 IRS Estimated Tax Reserve Due", {
          body: `${quarterLabel} · $${bufferDollars.toLocaleString(undefined, { maximumFractionDigits: 2 })} reserved from Cash Flash buffer · file by ${nextDeadline.toLocaleDateString()}`,
          tag: dispatchKey,
        });
        localStorage.setItem(dispatchKey, String(Date.now()));
      } catch { /* browser blocked */ }
    }, delay);
    return () => clearTimeout(id);
  }, [bufferCents, bufferDollars, quarterLabel, nextDeadline]);
  const askPermission = async () => {
    if (typeof Notification === "undefined") {
      toast.error("This browser does not support notifications");
      return;
    }
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === "granted") toast.success("🔔 Quarterly reminders armed");
      else toast.error("Notifications denied · reminders will show as toasts only");
    } catch (e) {
      toast.error("Permission request failed");
    }
  };
  if (bufferCents <= 0) return null;
  const bucket = (bufferDollars / 4).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return (
    <div
      className="mt-4 rounded-xl p-3 border border-amber-500/40"
      data-testid="cash-flash-quarterly-reminder"
      style={{
        background: "linear-gradient(90deg, rgba(255,176,0,0.06), rgba(255,215,0,0.04))",
        boxShadow: "inset 0 0 0 1px rgba(255,176,0,0.10)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: "#fcd34d" }}>
            🗓 {quarterLabel} · Estimated Tax Reserve Due
          </div>
          <div className="text-[11px] font-mono text-slate-200 mt-1">
            Next deadline · <span className="text-white">{nextDeadline.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })}</span>
            <span className="text-slate-500 mx-2">·</span>
            <span className="text-amber-200">{daysUntil} days</span>
            <span className="text-slate-500 mx-2">·</span>
            <span className="text-white">${bucket}</span>
            <span className="text-slate-500 ml-1">quarterly bucket</span>
          </div>
        </div>
        {permission === "granted" ? (
          <span
            className="text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md"
            data-testid="cash-flash-reminder-armed"
            style={{
              background: "rgba(16,185,129,0.14)",
              border: "1px solid rgba(16,185,129,0.55)",
              color: "#6ee7b7",
              boxShadow: "0 0 10px rgba(16,185,129,0.35)",
            }}
          >
            🔔 Reminder Armed
          </span>
        ) : (
          <button
            type="button"
            onClick={askPermission}
            data-testid="cash-flash-reminder-enable"
            className="text-[9px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-md"
            style={{
              background: "rgba(255,176,0,0.18)",
              border: "1px solid rgba(255,176,0,0.60)",
              color: "#fcd34d",
              boxShadow: "0 0 10px rgba(255,176,0,0.35)",
            }}
          >
            🔔 Enable Reminders
          </button>
        )}
      </div>
    </div>
  );
}
